import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAccountExpense,
  updateAccountExpense,
  getAccountExpenseById,
  getAccountExpenses,
  getAccountVendors,
  getAccountCustomers,
  getVesselList,
  getVesselAccountCodes,
} from '../core/_requests';
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {
  STANDARD_EXPENSE_ACCOUNTS,
  getCustomAccountTypes,
  saveCustomAccountType,
  isCustomAccountType,
} from '../core/expenseAccountTypes'
import {
  CONFIGURED_GST_RATES,
  calculateGstAmount,
  calculateExpenseFinalTotal,
  formatExpenseCurrency,
} from '../core/expenseTax'
import { Plus, X, Sparkles, Check, Calculator } from 'lucide-react'

export default function AccountsNewExpense() {
  const navigate = useNavigate();
  const { id } = useParams();
  const expenseId = id ? Number(id) : null;
  const isEditing = expenseId !== null && !Number.isNaN(expenseId);
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Vessel & Account Code State
  const [vessels, setVessels] = useState<any[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [selectedVesselName, setSelectedVesselName] = useState('');
  const [selectedAccountCode, setSelectedAccountCode] = useState('');
  const [availableAccountCodes, setAvailableAccountCodes] = useState<string[]>([]);
  const [loadingAccountCodes, setLoadingAccountCodes] = useState(false);

  // External Data
  const [vendors, setVendors] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  // Custom Account Types State
  const [customAccounts, setCustomAccounts] = useState<string[]>(() => getCustomAccountTypes());
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const [customAccountInput, setCustomAccountInput] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);

  // Form State
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [account, setAccount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [taxName, setTaxName] = useState('GST @ 18%');
  const [taxAmount, setTaxAmount] = useState('0.00');
  const [gstRate, setGstRate] = useState<string>('18');
  const [customRate, setCustomRate] = useState<string>('');
  const [isManualTax, setIsManualTax] = useState<boolean>(false);
  const [paidThrough, setPaidThrough] = useState('Petty Cash');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    // Load vendors, customers, and any existing custom accounts from expenses
    Promise.all([
      getAccountVendors().catch(() => []),
      getAccountCustomers().catch(() => []),
      getAccountExpenses().catch(() => []),
    ]).then(([vData, cData, expData]) => {
      setVendors(vData || []);
      setCustomers(cData || []);
      if (Array.isArray(expData)) {
        expData.forEach((item: any) => {
          if (item?.account && isCustomAccountType(item.account)) {
            saveCustomAccountType(item.account);
          }
        });
        setCustomAccounts(getCustomAccountTypes());
      }
    });

    if (globalVessels && globalVessels.length > 0) {
      setVessels(globalVessels);
    } else {
      getVesselList()
        .then((data) => {
          if (Array.isArray(data)) setVessels(data);
        })
        .catch((err) => console.error('Failed to load vessels:', err));
    }
  }, [globalVessels]);

  // Default vessel when creating a new expense if global filter vessel is selected
  useEffect(() => {
    if (!isEditing && !selectedVesselId && globalState.vessel?.id) {
      setSelectedVesselId(String(globalState.vessel.id));
      setSelectedVesselName(globalState.vessel.fleetName || (globalState.vessel as any).fleet_name || '');
    }
  }, [isEditing, globalState.vessel, selectedVesselId]);

  // Load account codes based on selected vessel and company
  const loadAccountCodesForVessel = async (vesselId: string | number) => {
    if (!vesselId) {
      setAvailableAccountCodes([]);
      return;
    }
    setLoadingAccountCodes(true);
    try {
      const response = await getVesselAccountCodes(vesselId, globalState.company?.id);
      const data = response?.data || response || [];
      const rows = Array.isArray(data) ? data : [];
      const codes = Array.from(
        new Set(
          rows
            .map((r: any) => (r.accountCodes || r.account_codes || r.accountCode || '').trim())
            .filter(Boolean)
        )
      ) as string[];
      setAvailableAccountCodes(codes);
    } catch (err) {
      console.error('Failed to fetch account codes:', err);
      setAvailableAccountCodes([]);
    } finally {
      setLoadingAccountCodes(false);
    }
  };

  useEffect(() => {
    if (selectedVesselId) {
      loadAccountCodesForVessel(selectedVesselId);
    } else {
      setAvailableAccountCodes([]);
    }
  }, [selectedVesselId, globalState.company?.id]);

  const handleVesselChange = (vesselId: string) => {
    setSelectedVesselId(vesselId);
    const vessel = vessels.find((v: any) => String(v.id) === String(vesselId));
    const vName = vessel?.fleetName || vessel?.fleet_name || vessel?.name || '';
    setSelectedVesselName(vName);
    setSelectedAccountCode('');
  };

  useEffect(() => {
    if (!isEditing || expenseId == null) return;
    setLoadingInitial(true);
    getAccountExpenseById(expenseId).then(data => {
      if (data) {
        setExpenseDate(data.expenseDate || data.expense_date || new Date().toISOString().split('T')[0]);
        const vId = data.vesselId || data.vessel_id ? String(data.vesselId || data.vessel_id) : '';
        const vName = data.vesselName || data.vessel_name || '';
        const accCode = data.accountCode || data.account_code || '';
        setSelectedVesselId(vId);
        setSelectedVesselName(vName);
        setSelectedAccountCode(accCode);
        if (vId) {
          loadAccountCodesForVessel(vId);
        }
        const acc = data.account || '';
        setAccount(acc);
        if (acc && isCustomAccountType(acc)) {
          saveCustomAccountType(acc);
          setCustomAccounts(getCustomAccountTypes());
        }
        setReferenceNumber(data.referenceNumber || data.reference_number || '');
        setVendorId(data.vendorId ? String(data.vendorId) : '');
        setCustomerId(data.customerId ? String(data.customerId) : '');
        const loadedAmount = data.amount !== undefined ? String(data.amount) : '';
        const loadedTaxName = data.taxName || data.tax_name || '';
        const loadedTaxAmount = data.taxAmount !== undefined ? String(data.taxAmount) : '';
        setAmount(loadedAmount);
        setTaxName(loadedTaxName);
        setTaxAmount(loadedTaxAmount);

        // Derive matching GST rate if possible
        const b = Number(loadedAmount) || 0;
        const t = Number(loadedTaxAmount) || 0;
        if (b > 0 && t > 0) {
          const derivedPct = Math.round((t / b) * 100 * 100) / 100;
          const match = CONFIGURED_GST_RATES.find(r => r.rate === derivedPct);
          if (match) {
            setGstRate(String(match.rate));
            setIsManualTax(false);
          } else {
            setGstRate('custom');
            setCustomRate(String(derivedPct));
            setIsManualTax(false);
          }
        } else if (t === 0 && (loadedTaxName || loadedAmount)) {
          setGstRate('0');
          setIsManualTax(false);
        } else {
          setIsManualTax(true);
        }

        setPaidThrough(data.paidThrough || data.paid_through || 'Petty Cash');
        setNotes(data.notes || '');
      }
    }).catch(console.error).finally(() => setLoadingInitial(false));
  }, [isEditing, expenseId]);

  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (!isManualTax) {
      const activeRate = gstRate === 'custom' ? Number(customRate) || 0 : Number(gstRate);
      const calc = calculateGstAmount(newAmount, activeRate);
      setTaxAmount(calc > 0 ? calc.toFixed(2) : '0.00');
      if (activeRate > 0) {
        setTaxName(`GST @ ${activeRate}%`);
      } else {
        setTaxName('GST 0% (Exempt)');
      }
    }
  };

  const handleGstRateChange = (newRate: string) => {
    setGstRate(newRate);
    if (newRate === 'manual') {
      setIsManualTax(true);
      return;
    }
    setIsManualTax(false);
    if (newRate === 'custom') {
      const activeRate = Number(customRate) || 0;
      const calc = calculateGstAmount(amount, activeRate);
      setTaxAmount(calc > 0 ? calc.toFixed(2) : '0.00');
      setTaxName(activeRate > 0 ? `GST @ ${activeRate}%` : 'GST Custom');
    } else {
      const activeRate = Number(newRate);
      const calc = calculateGstAmount(amount, activeRate);
      setTaxAmount(calc > 0 ? calc.toFixed(2) : '0.00');
      setTaxName(activeRate > 0 ? `GST @ ${activeRate}%` : 'GST 0% (Exempt)');
    }
  };

  const handleCustomRateChange = (rateStr: string) => {
    setCustomRate(rateStr);
    const activeRate = Number(rateStr) || 0;
    const calc = calculateGstAmount(amount, activeRate);
    setTaxAmount(calc > 0 ? calc.toFixed(2) : '0.00');
    setTaxName(activeRate > 0 ? `GST @ ${activeRate}%` : 'GST Custom');
  };

  const handleCreateCustomAccount = () => {
    const trimmed = customAccountInput.trim();
    if (!trimmed) {
      setCustomError('Please enter an account type name.');
      return;
    }
    const updated = saveCustomAccountType(trimmed);
    setCustomAccounts(updated);
    setAccount(trimmed);
    setIsCreatingCustom(false);
    setCustomAccountInput('');
    setCustomError(null);
  };

  const handleSave = async () => {
    let effectiveAccount = account;
    if (isCreatingCustom && customAccountInput.trim()) {
      effectiveAccount = customAccountInput.trim();
      saveCustomAccountType(effectiveAccount);
      setCustomAccounts(getCustomAccountTypes());
      setAccount(effectiveAccount);
    }

    if (!expenseDate || !effectiveAccount || !amount) {
      alert("Please fill in all required fields (Date, Account, Amount).");
      return;
    }

    if (isCustomAccountType(effectiveAccount)) {
      saveCustomAccountType(effectiveAccount);
    }
    
    setSaving(true);
    const selectedVendor = vendors.find(v => v.id === Number(vendorId));
    const selectedCustomer = customers.find(c => c.id === Number(customerId));
    const matchedVessel = vessels.find(v => String(v.id) === String(selectedVesselId));
    const vName = selectedVesselName || matchedVessel?.fleetName || matchedVessel?.fleet_name || matchedVessel?.name || null;

    const payload = {
      expenseDate,
      vesselId: selectedVesselId ? Number(selectedVesselId) : null,
      vesselName: selectedVesselId ? vName : null,
      accountCode: selectedAccountCode || null,
      account: effectiveAccount,
      referenceNumber,
      vendorId: vendorId ? Number(vendorId) : null,
      vendorName: selectedVendor ? (selectedVendor.displayName || selectedVendor.companyName || selectedVendor.name) : null,
      customerId: customerId ? Number(customerId) : null,
      customerName: selectedCustomer ? selectedCustomer.displayName : null,
      amount: Number(amount),
      taxName,
      taxAmount: taxAmount ? Number(taxAmount) : 0,
      paidThrough,
      notes
    };

    try {
      if (isEditing && expenseId) {
        await updateAccountExpense(expenseId, payload);
      } else {
        await createAccountExpense(payload);
      }
      navigate('/accounts/purchase/expenses');
    } catch (err: any) {
      console.error('Save failed:', err);
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all";

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Expense...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Purchase / Expenses</div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? 'Edit Expense' : 'New Expense'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? 'Update the details of your expense' : 'Record a new expense'}
                </p>
              </div>
              
              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Date <span className="text-red-500">*</span></label>
                    <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} required className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Expense Account <span className="text-red-500">*</span>
                        {account && isCustomAccountType(account) && (
                          <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Custom Account
                          </span>
                        )}
                      </label>
                      {!isCreatingCustom && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsCreatingCustom(true);
                            setCustomAccountInput('');
                            setCustomError(null);
                          }}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Custom Account Type
                        </button>
                      )}
                    </div>
                    <SearchableSelect
                      value={account}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === '__NEW_CUSTOM__') {
                          setIsCreatingCustom(true);
                          setCustomAccountInput('');
                          setCustomError(null);
                        } else {
                          setAccount(val);
                          setIsCreatingCustom(false);
                        }
                      }}
                      required
                      className={inputClass}
                    >
                      <option value="">Select an account</option>
                      {STANDARD_EXPENSE_ACCOUNTS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      {customAccounts.length > 0 && (
                        <optgroup label="Custom Account Types">
                          {customAccounts.map((opt) => (
                            <option key={opt} value={opt}>{opt} (Custom)</option>
                          ))}
                        </optgroup>
                      )}
                      <option value="__NEW_CUSTOM__">+ Add Custom Account Type...</option>
                    </SearchableSelect>

                    {isCreatingCustom && (
                      <div className="mt-2.5 p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2.5 animate-fadeIn">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-900 uppercase tracking-wider">
                            <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                            Create Custom Account Type
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setIsCreatingCustom(false);
                              setCustomAccountInput('');
                              setCustomError(null);
                            }}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customAccountInput}
                            onChange={(e) => {
                              setCustomAccountInput(e.target.value);
                              setCustomError(null);
                            }}
                            placeholder="Enter any account type name (e.g. Port Dues, Crew Welfare...)"
                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateCustomAccount();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleCreateCustomAccount}
                            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow-xs transition-all flex items-center gap-1.5 shrink-0"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Save & Select
                          </button>
                        </div>
                        {customError && (
                          <p className="text-xs text-red-600 font-medium">{customError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Expense Amount (Subtotal) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={e => handleAmountChange(e.target.value)}
                      placeholder="0.00"
                      required
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Paid Through <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect value={paidThrough} onChange={e => setPaidThrough(e.target.value)} required className={inputClass}>
                      <option value="Petty Cash">Petty Cash</option>
                      <option value="Undeposited Funds">Undeposited Funds</option>
                      <option value="Bank Account">Bank Account</option>
                      <option value="Credit Card">Credit Card</option>
                    </SearchableSelect>
                  </div>
                </div>
                
                {/* GST Calculation & Review Section */}
                <div className="bg-slate-50/80 border border-slate-200/90 rounded-2xl p-5 mb-6 space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                        GST Calculation
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-slate-500">
                      Calculates GST on base amount using configured rates
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                        Applicable GST Rate <span className="text-red-500">*</span>
                      </label>
                      <SearchableSelect
                        value={isManualTax ? 'manual' : gstRate}
                        onChange={e => handleGstRateChange(e.target.value)}
                        className={inputClass}
                      >
                        {CONFIGURED_GST_RATES.map((opt) => (
                          <option key={opt.rate} value={String(opt.rate)}>
                            {opt.label}
                          </option>
                        ))}
                        <option value="custom">Custom GST Rate %</option>
                        <option value="manual">Manual Tax Entry</option>
                      </SearchableSelect>
                    </div>

                    {gstRate === 'custom' && !isManualTax ? (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                          Custom Rate (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={customRate}
                            onChange={e => handleCustomRateChange(e.target.value)}
                            placeholder="e.g. 7.5"
                            className={inputClass}
                          />
                          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                          Tax / GST Name
                        </label>
                        <input
                          type="text"
                          value={taxName}
                          onChange={e => {
                            setTaxName(e.target.value);
                            setIsManualTax(true);
                          }}
                          placeholder="e.g. GST @ 18%"
                          className={inputClass}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider flex items-center justify-between">
                        <span>GST Amount</span>
                        {!isManualTax && Number(taxAmount) > 0 && (
                          <span className="text-[10px] text-emerald-600 font-bold">Auto-calculated</span>
                        )}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxAmount}
                        onChange={e => {
                          setTaxAmount(e.target.value);
                          setIsManualTax(true);
                        }}
                        placeholder="0.00"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Summary & Review Card */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 mt-2 shadow-xs">
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                      Expense & GST Summary
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center sm:text-left">
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <span className="text-[11px] text-slate-500 font-medium block">Expense Subtotal</span>
                        <span className="text-base font-bold text-slate-800 font-mono">
                          {formatExpenseCurrency(Number(amount) || 0)}
                        </span>
                      </div>
                      <div className="p-3 bg-emerald-50/60 rounded-lg border border-emerald-100">
                        <span className="text-[11px] text-emerald-700 font-medium block">
                          GST Amount {taxName ? `(${taxName})` : ''}
                        </span>
                        <span className="text-base font-bold text-emerald-600 font-mono">
                          +{formatExpenseCurrency(Number(taxAmount) || 0)}
                        </span>
                      </div>
                      <div className="p-3 bg-blue-50/70 rounded-lg border border-blue-200">
                        <span className="text-[11px] text-blue-800 font-semibold block">Final Expense Total</span>
                        <span className="text-xl font-black text-blue-700 font-mono">
                          {formatExpenseCurrency(calculateExpenseFinalTotal(amount, taxAmount))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-6 border-slate-100" />

                {/* Vessel & Budget Account Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Vessel <span className="text-slate-400 font-normal lowercase">(optional)</span>
                    </label>
                    <SearchableSelect
                      value={selectedVesselId}
                      onChange={(e) => handleVesselChange(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">-- Choose Vessel --</option>
                      {vessels.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {v.fleetName || v.fleet_name || v.name || v.imoNumber || `Vessel #${v.id}`}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Account Code (Vessel Budget) <span className="text-slate-400 font-normal lowercase">(optional)</span>
                    </label>
                    <SearchableSelect
                      value={selectedAccountCode}
                      onChange={(e) => setSelectedAccountCode(e.target.value)}
                      className={inputClass}
                      disabled={!selectedVesselId && availableAccountCodes.length === 0}
                    >
                      <option value="">
                        {loadingAccountCodes
                          ? 'Loading account codes...'
                          : selectedVesselId
                            ? '-- Select Account Code --'
                            : 'Select vessel first'}
                      </option>
                      {availableAccountCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                      {selectedAccountCode && !availableAccountCodes.includes(selectedAccountCode) && (
                        <option value={selectedAccountCode}>{selectedAccountCode}</option>
                      )}
                    </SearchableSelect>
                    {selectedAccountCode && (
                      <p className="text-[11px] text-emerald-600 font-medium mt-1">
                        * This expense will be deducted from the vessel budget for {selectedAccountCode}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Vendor</label>
                    <SearchableSelect value={vendorId} onChange={e => setVendorId(e.target.value)} className={inputClass}>
                      <option value="">Select a vendor...</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.displayName}</option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Reference#</label>
                    <input type="text" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} placeholder="Reference Number" className={inputClass} />
                  </div>
                </div>

                <div className="mb-5 max-w-md">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Customer Name</label>
                  <SearchableSelect value={customerId} onChange={e => setCustomerId(e.target.value)} className={inputClass}>
                    <option value="">Select a customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                  </SearchableSelect>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Max. 500 characters" className={`${inputClass} resize-none`}></textarea>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Expense'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

